import { useState } from "react";
import { api, errorText } from "./api";
import { Brand } from "./components/Icons";
import { ThemePicker } from "./components/ThemePicker";

/* The apex.

   It used to be the claim form, which meant the only people the front door
   worked for were the ones already holding a code — everybody else arrived at a
   field they could not fill in and left. So the form moved one click away and
   this took its place: what the thing is, and the two doors out of it.

   "I have a code" goes to /claim. "I want one" writes a row the operator's
   console lists, because the honest version of a waitlist is a queue somebody
   reads, not an address that forwards to an inbox nobody opens. */

interface LandingProps {
  baseDomain: string;
  onSetUp: () => void;
}

export function Landing({ baseDomain, onSetUp }: LandingProps) {
  return (
    <main className="landing">
      <header className="landing-head">
        <Brand name="ToonSpeech" />
        <ThemePicker />
      </header>

      <section className="hero">
        <h1>
          Your chat types. <span className="accent">Your character speaks.</span>
        </h1>
        <p className="lede">
          TTS for your stream. Chat sends a message, ElevenLabs reads it out,
          and your PNGtuber lip-syncs along in OBS.
        </p>
        <div className="hero-actions">
          <button className="primary" onClick={onSetUp}>
            I have an invite code
          </button>
          <a className="ghost-link" href="#request">
            Ask for one
          </a>
        </div>
        <p className="muted hero-note">
          You get <code>yourname.{baseDomain}</code>.
        </p>
      </section>

      <section className="features">
        <Feature title="Bring your own key">
          Uses your ElevenLabs account, billed directly. The key is stored
          encrypted.
        </Feature>
        <Feature title="No accounts">
          One password for chat, one admin password for you.
        </Feature>
        <Feature title="Multiple characters">
          Save voices as characters. Each one can get its own OBS source.
        </Feature>
        <Feature title="Moderation">
          Lock the panel, mute people, skip clips and set character limits.
        </Feature>
      </section>

      <RequestForm />

      <footer className="landing-foot">
        <span className="muted">ToonSpeech · {baseDomain}</span>
      </footer>
    </main>
  );
}

function Feature({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="feature">
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

/* The ask.

   Deliberately three fields and no account: a name, a way to be reached, and
   why. Anything more is a form people abandon, and the operator is going to
   answer it with a Discord message either way. */
function RequestForm() {
  const [handle, setHandle] = useState("");
  const [contact, setContact] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (sent) {
    return (
      <section className="request-card sent" id="request">
        <h2>Request sent</h2>
        <p>We'll get back to you soon.</p>
      </section>
    );
  }

  return (
    <section className="request-card" id="request">
      <h2>Request access</h2>
      <p className="muted">ToonSpeech is invite-only for now.</p>

      <form
        className="claim-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api("/api/request-access", {
              method: "POST",
              body: JSON.stringify({
                handle: handle.trim(),
                contact: contact.trim(),
                note: note.trim(),
              }),
            });
            setSent(true);
          } catch (caught) {
            setError(errorText(caught));
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Channel or vtuber name
          <input
            value={handle}
            onChange={(event) => setHandle(event.target.value)}
            placeholder="Rinu"
            maxLength={60}
            required
          />
        </label>

        <label>
          Where to reach you
          <input
            value={contact}
            onChange={(event) => setContact(event.target.value)}
            placeholder="Discord handle or email"
            maxLength={120}
            required
          />
        </label>

        <label>
          Anything else <span className="muted">(optional)</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Where do you stream?"
            maxLength={500}
            rows={3}
          />
        </label>

        {error && <p className="bad">{error}</p>}

        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send request"}
        </button>
      </form>
    </section>
  );
}
